import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Eye, Loader2, Paperclip, Trash2, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const EldenFilesModal = ({ row, yil, ay, files, onChanged, onClose }) => {
  const { isAdmin } = useAuth();
  const inp = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = async (list) => {
    if (!list?.length) return;
    setBusy(true);
    try {
      for (const f of list) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: 10 MB'tan büyük`); continue; }
        const fd = new FormData();
        fd.append("personnel_id", row.personnel_id); fd.append("yil", yil); fd.append("ay", ay); fd.append("file", f);
        setProgress(0);
        await api.post("/payroll/elden-files", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
        });
        toast.success(`${f.name} yüklendi`);
      }
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yüklenemedi");
    } finally { setBusy(false); if (inp.current) inp.current.value = ""; }
  };

  const view = async (f) => {
    try {
      const r = await api.get(`/payroll/elden-files/${f.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: f.content_type })), "_blank");
    } catch { toast.error("Dosya açılamadı"); }
  };
  const remove = async (f) => {
    if (!window.confirm(`${f.original_filename} silinsin mi?`)) return;
    try { await api.delete(`/payroll/elden-files/${f.id}`); toast.success("Silindi"); onChanged(); }
    catch { toast.error("Silinemedi"); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="elden-files-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="p-3 border-b flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">İmzalı Elden Makbuzu</div>
            <div className="text-sm font-bold text-slate-900">{row.ad_soyad}</div>
          </div>
          <button onClick={onClose} data-testid="elden-files-close" className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); upload([...e.dataTransfer.files]); }}
            onClick={() => !busy && inp.current?.click()}
            data-testid="elden-files-dropzone"
            className="border-2 border-dashed border-violet-300 bg-violet-50/50 hover:bg-violet-50 rounded-lg p-5 text-center cursor-pointer"
          >
            {busy ? (
              <div className="text-sm text-violet-800"><Loader2 size={16} className="inline animate-spin mr-1" /> Yükleniyor... %{progress}</div>
            ) : (
              <div className="text-sm text-violet-800"><Upload size={16} className="inline mr-1" /> Taranmış makbuzu sürükleyin veya tıklayın
                <div className="text-[11px] text-violet-600 mt-0.5">PDF, JPG veya PNG · en fazla 10 MB · birden fazla dosya olabilir</div>
              </div>
            )}
            <input ref={inp} type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid="elden-files-input"
              onChange={(e) => upload([...e.target.files])} />
          </div>
          {files.length === 0 ? (
            <div className="text-xs text-slate-400 text-center" data-testid="elden-files-empty">Henüz imzalı makbuz yüklenmemiş</div>
          ) : files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2 py-1.5 text-xs" data-testid={`elden-file-${f.id}`}>
              <Paperclip size={13} className="text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate" title={f.original_filename}>{f.original_filename}</div>
                <div className="text-[10px] text-slate-500">{kb(f.size)} · {new Date(f.created_at).toLocaleString("tr-TR")} · {f.created_by_name}</div>
              </div>
              <button onClick={() => view(f)} data-testid={`elden-file-view-${f.id}`} title="Görüntüle" className="p-1.5 bg-sky-100 hover:bg-sky-200 text-sky-700 rounded"><Eye size={13} /></button>
              {isAdmin && <button onClick={() => remove(f)} data-testid={`elden-file-delete-${f.id}`} title="Sil" className="p-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded"><Trash2 size={13} /></button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
